import React, { useState, useMemo } from 'react';
import { Project } from '../types';

interface ProjectsPageProps {
  projects: Project[];
  onSelectProject: (project: Project) => void;
  onCreateProject: () => void;
  onToggleSidebar?: () => void;
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return 'Jun 30';
  try {
    const date = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  } catch {
    return 'Jun 30';
  }
};



const ProjectsPage: React.FC<ProjectsPageProps> = ({ projects, onSelectProject, onCreateProject, onToggleSidebar }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProjects = useMemo(() => {
    return projects.filter((project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [projects, searchQuery]);

  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-[#050505] animate-in fade-in duration-300">
      
      {/* Header */}
      <div className="flex items-center justify-between px-6 md:px-10 py-6 md:py-8 border-b border-gray-100 dark:border-[#1d1d1f] flex-shrink-0">
        <div className="flex items-center gap-3">
          {onToggleSidebar && (
            <button
              onClick={onToggleSidebar}
              className="md:hidden p-2 -ml-2 text-gray-900 dark:text-white hover:bg-gray-105 dark:hover:bg-[#212121] rounded-xl cursor-pointer"
            >
              <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <line x1="4" y1="12" x2="20" y2="12"/>
                <line x1="4" y1="6" x2="20" y2="6"/>
                <line x1="4" y1="18" x2="20" y2="18"/>
              </svg>
            </button>
          )}
          <h2 className="text-2xl font-normal text-gray-900 dark:text-white tracking-tight">Projects</h2>
        </div>
      </div>

      {/* Sub Header Content (Pill + Column Labels) */}
      <div className="flex flex-col gap-6 px-10 pt-6 pb-2 flex-shrink-0">
        {/* Created by you pill indicator & Search/New Actions Row */}
        <div className="flex items-center justify-between border-b border-zinc-100 dark:border-zinc-900 pb-3 flex-wrap gap-3">
          <span className="px-4 py-1.5 bg-zinc-100 dark:bg-zinc-900 text-zinc-800 dark:text-zinc-200 font-medium rounded-full text-xs font-sans select-none border border-transparent">
            Created by you
          </span>

          <div className="flex items-center gap-3">
            {/* Search input bar */}
            <div className="relative">
              <svg 
                className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.3-4.3"/>
              </svg>
              <input
                type="text"
                placeholder="Search projects"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-64 bg-zinc-55 dark:bg-[#0a0a0a] border border-zinc-200 dark:border-zinc-800 rounded-full text-sm text-zinc-900 dark:text-white outline-none focus:border-zinc-400 dark:focus:border-zinc-600 transition-colors"
              />
            </div>
            
            {/* New Button */}
            <button
              onClick={onCreateProject}
              className="px-5 py-2 bg-black hover:bg-zinc-850 dark:bg-white dark:hover:bg-zinc-150 text-white dark:text-black font-medium rounded-full text-sm shadow-sm transition-all flex items-center gap-1.5"
            >
              <span>+</span>
              <span>New</span>
            </button>
          </div>
        </div>

        {/* Column Labels Row */}
        <div className="flex justify-between items-center text-xs font-semibold text-zinc-400 dark:text-zinc-500 tracking-wider px-2 font-sans select-none">
          <span>Name</span>
        </div>
      </div>

      {/* Grid Content container */}
      <div className="flex-1 overflow-y-auto px-10 pb-10 pt-4 no-scrollbar">
        {filteredProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 border border-dashed border-zinc-200 dark:border-zinc-800 rounded-2xl bg-zinc-50/30 dark:bg-transparent">
            <svg 
              className="w-10 h-10 text-zinc-400 mb-3" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              strokeWidth="1.5"
            >
              <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>
            </svg>
            <span className="text-zinc-500 dark:text-zinc-400 text-sm font-medium">No projects found</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl">
            {filteredProjects.map((project) => {
              const isActive = project.status === 'running';
              const isWaiting = project.status === 'waiting';
              
              return (
                <div 
                  key={project.id}
                  className="group flex flex-col justify-between min-h-[260px] rounded-2xl border border-zinc-150 dark:border-zinc-800 bg-white dark:bg-black hover:border-zinc-300 dark:hover:border-zinc-700 shadow-[0_1px_3px_rgba(0,0,0,0.02)] dark:shadow-none hover:shadow-md transition-all duration-300 relative overflow-hidden"
                >
                  {/* Outer Wrapper */}
                  <div className="p-6 flex-1 flex flex-col justify-between">
                    
                    {/* Top Section */}
                    <div className="flex justify-between items-center gap-4">
                      <h3 className="font-normal text-zinc-900 dark:text-zinc-50 text-base font-sans line-clamp-1 leading-snug">
                        {project.name}
                      </h3>
                      
                      {/* Live Runtime status badge */}
                      <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800">
                        <span className="relative flex h-1.5 w-1.5">
                          {isActive && (
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-zinc-955 dark:bg-white opacity-75"></span>
                          )}
                          <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${isActive ? 'bg-zinc-950 dark:bg-white' : 'bg-zinc-400 dark:bg-zinc-650'}`}></span>
                        </span>
                        <span className="text-[10px] font-medium tracking-wider uppercase text-zinc-700 dark:text-zinc-300 select-none">
                          {isActive ? 'Active' : isWaiting ? 'Waiting' : 'Complete'}
                        </span>
                      </div>
                    </div>

                    {/* Body Section */}
                    <div className="flex flex-col gap-3 my-4 flex-1 justify-center">
                      <div className="flex justify-between items-start text-xs leading-relaxed">
                        <span className="text-zinc-400 dark:text-zinc-500 font-medium">Current Task</span>
                        <span className="text-zinc-800 dark:text-zinc-200 font-medium text-right max-w-[70%]">
                          {project.next_action || 'Review initial plan outline.'}
                        </span>
                      </div>

                      <div className="flex justify-between items-start text-xs border-t border-zinc-100 dark:border-zinc-900 pt-3 leading-relaxed">
                        <span className="text-zinc-400 dark:text-zinc-500 font-medium">Status</span>
                        <span className="text-zinc-800 dark:text-zinc-200 font-medium text-right max-w-[70%]">
                          {project.situation || 'Awaiting automated triggers...'}
                        </span>
                      </div>
                    </div>

                    {/* Actions and Progress Footer */}
                    <div className="flex items-end justify-between mt-auto pt-3 border-t border-zinc-100 dark:border-zinc-900">
                      <div className="flex flex-col gap-0.5 select-none">
                        <span className="text-[9px] font-medium text-zinc-450 dark:text-zinc-500 uppercase tracking-widest">
                          Progress
                        </span>
                        <span className="text-sm font-medium text-zinc-900 dark:text-white font-sans">
                          {project.progress || 0}%
                        </span>
                      </div>

                      <div className="flex items-center gap-4">
                        <span className="text-[11px] text-zinc-400 dark:text-zinc-500 font-sans select-none">
                          Modified: {formatDate(project.updated_at || project.created_at)}
                        </span>
                        
                        {/* Open Action Button */}
                        <button
                          onClick={() => onSelectProject(project)}
                          className="px-4 py-1.5 bg-zinc-900 hover:bg-zinc-850 text-white dark:bg-white dark:hover:bg-zinc-200 dark:text-black border border-transparent text-xs font-medium rounded-lg hover:shadow-sm transition-all flex items-center gap-1 cursor-pointer"
                        >
                          <span>Open</span>
                          <svg className="w-3 h-3 transition-transform group-hover:translate-x-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="9 18 15 12 9 6"/>
                          </svg>
                        </button>
                      </div>
                    </div>

                  </div>

                  {/* Bottom Border Metric Progress Bar */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-100 dark:bg-zinc-900">
                    <div 
                      className="h-full bg-zinc-900 dark:bg-zinc-100 transition-all duration-500 ease-out rounded-r-full"
                      style={{ width: `${project.progress || 0}%` }}
                    />
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
};

export default ProjectsPage;
